import { runStrategy } from '@luna/utils';
import type { ApiSelector, ApiSliceKey } from '@luna/api';
import { logger } from '@luna/logger';

import type {
    EnrichProductParameter,
    ProductConfiguration,
    ProductMethods,
    ProductState,
    ProductStrategy,
} from '../configurations/productConfigurationRegistry';
import { getAllProductConfiguration } from '../configurations/productConfigurationRegistry';
import { runBaseEnrichmentsOnProduct } from './baseEnrichmentService';
import type {
    AccountOrAccountOfExternalProduct,
    ProductWithHelpers,
} from '../types/productHelpers';

const getRegisteredConfiguration = <T>(): ProductConfiguration<T>[] =>
    Object.values(getAllProductConfiguration()) as ProductConfiguration<T>[];

export const getProductStrategies = <T>(): ProductStrategy<T>[] => {
    return getRegisteredConfiguration<T>().map((configuration) => configuration.productStrategy);
};
export const enrichProducts = <BaseType extends AccountOrAccountOfExternalProduct, UnderlyingData>(
    products: BaseType[],
): ProductWithHelpers<BaseType, UnderlyingData>[] => {
    const enrichStrategies = getProductStrategies<BaseType>();
    return products.map((product) => {
        const baseEnrichedProduct = runBaseEnrichmentsOnProduct({
            product: product as ProductWithHelpers<BaseType>,
        });
        return runStrategy(enrichStrategies, {
            arg: { products, product } as EnrichProductParameter<BaseType>,
            stopAfterFirstRun: false,
        })
            .map((strategy) => strategy.enrichProduct({ product: baseEnrichedProduct, products }))
            .reduce(
                (prev, curr) => ({
                    ...prev,
                    ...curr,
                }),
                product,
            ) as ProductWithHelpers<BaseType, UnderlyingData>;
    });
};

export const retrieveMergedSelectors = (): ApiSelector => {
    return () => getRegisteredConfiguration().map((config) => config.dataSelector());
};

export const retrieveMergedSliceIds = (): ApiSliceKey[] => {
    return getRegisteredConfiguration().map((config) => config.dataSelectorId);
};

export const queryRegisteredProductsIfNeeded = (): Promise<any[]> => {
    return Promise.all(
        getRegisteredConfiguration().map((config) =>
            config.queryAllIfNeeded().catch((error) => {
                logger.error(
                    `Fetching product with selector ${config.dataSelectorId} failed`,
                    error,
                );
                throw error;
            }),
        ),
    );
};

export const queryRegisteredProducts = (): Promise<any[]> => {
    return Promise.all(
        getRegisteredConfiguration().map((config) =>
            config.queryAll().catch((error) => {
                logger.error(
                    `Fetching product with selector ${config.dataSelectorId} failed`,
                    error,
                );
                throw error;
            }),
        ),
    );
};

const fallbackStateSelector = (): ProductState => ({
    isProductPending: false,
    error: undefined,
    didProductInvalidate: false,
});

export const getProductMethodsOrDefault = <BaseType, UnderlyingData>(
    product?: ProductWithHelpers<BaseType, UnderlyingData>,
    products?: ProductWithHelpers<BaseType, UnderlyingData>[],
): ProductMethods => {
    if (!product || !products) {
        return {
            refetch: (): Promise<void> =>
                Promise.reject(new Error('Product strategy not supported')),
            stateSelector: fallbackStateSelector,
            queryIfNeeded: () => Promise.resolve(),
            detailSliceId: [],
        };
    }

    const methods = runStrategy(getProductStrategies(), {
        arg: { products, product } as EnrichProductParameter<BaseType>,
    })?.getProductMethods({ product, products });

    return {
        refetch: () =>
            methods?.refetch() ?? Promise.reject(new Error('Product strategy not supported')),
        stateSelector: methods?.stateSelector ?? fallbackStateSelector,
        queryIfNeeded: () =>
            methods?.queryIfNeeded() ?? Promise.reject(new Error('Product strategy not supported')),
        detailSliceId: methods?.detailSliceId ?? [],
    };
};
